import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { contactFormSchema } from "../../utils/validationSchemas";
import { handleContactSubmit } from "../../utils/formHandlers";
import PropTypes from "prop-types";

const ContactForm = ({
  showContactForm,
  setShowContactForm,
  formSubmitted,
  setAlertMessage,
  setShowAlert,
  setFormSubmitted,
  initialMessage = "",
  setAlertType = null,
}) => {
  // const [error, setError] = React.useState("");

  const closeContactForm = () => {
    setShowContactForm(false);
    // setError("");
    setFormSubmitted(false);
  };

  return (
    <AnimatePresence>
      {showContactForm && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="w-full max-w-2xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="flex justify-between items-center p-6 border-b">
                <h3 className="text-2xl font-bold">Contact Us</h3>
                <button
                  onClick={closeContactForm}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <XMarkIcon
                    className="w-6 h-6"
                    data-testid="close-contact-form"
                  />
                </button>
              </div>

              <div className="p-6">
                {formSubmitted ? (
                  <div
                    className="text-center py-8"
                    data-testid="contact-success"
                  >
                    <svg
                      className="w-16 h-16 text-green-500 mx-auto mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                    <h4 className="text-2xl font-bold mb-2">Thank You!</h4>
                    <p className="text-gray-600">
                      {`Your message has been sent successfully. We'll get back to
                      you soon.`}
                    </p>
                  </div>
                ) : (
                  <Formik
                    initialValues={{
                      name: "",
                      email: "",
                      company: "",
                      message: initialMessage,
                    }}
                    validationSchema={contactFormSchema}
                    onSubmit={async (values, formikBag) => {
                      // setError("");
                      // try {
                      await handleContactSubmit(
                        values,
                        formikBag,
                        setAlertMessage,
                        setShowAlert,
                        setFormSubmitted,
                        setShowContactForm,
                        setAlertType
                      );
                      // } catch (err) {
                      //   setError(
                      //     err.message || "Failed to submit contact form"
                      //   );
                      // }
                    }}
                  >
                    {({ isSubmitting, errors, touched }) => (
                      <Form className="space-y-4">
                        {/* {error && (
                          <div
                            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative"
                            data-testid="contact-form-error"
                          >
                            {error}
                          </div>
                        )} */}

                        <div>
                          <label
                            htmlFor="name"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Name
                          </label>
                          <Field
                            type="text"
                            name="name"
                            id="name"
                            data-testid="contact-name"
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none ${
                              errors.name && touched.name
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            placeholder="Your name"
                          />
                          <ErrorMessage
                            name="name"
                            component="div"
                            data-testid="contact-name-error"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>

                        <div>
                          <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Email
                          </label>
                          <Field
                            type="email"
                            name="email"
                            id="email"
                            data-testid="contact-email"
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none ${
                              errors.email && touched.email
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            placeholder="your.email@example.com"
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            data-testid="contact-email-error"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>

                        <div>
                          <label
                            htmlFor="company"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Company
                          </label>
                          <Field
                            type="text"
                            name="company"
                            id="company"
                            data-testid="contact-company"
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none ${
                              errors.company && touched.company
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            placeholder="Your company (optional)"
                          />
                          <ErrorMessage
                            name="company"
                            component="div"
                            data-testid="contact-company-error"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>

                        <div>
                          <label
                            htmlFor="message"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Message
                          </label>
                          <Field
                            as="textarea"
                            name="message"
                            id="message"
                            data-testid="contact-message"
                            rows={4}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none ${
                              errors.message && touched.message
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            placeholder="How can we help you?"
                          />
                          <ErrorMessage
                            name="message"
                            component="div"
                            data-testid="contact-message-error"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>

                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => setShowContactForm(false)}
                            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg mr-2 hover:bg-gray-50"
                            data-testid="contact-cancel"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            data-testid="contact-submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 bg-primary text-black bg-peachYellow rounded-lg hover:bg-peachYellow/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
                          >
                            {isSubmitting ? (
                              <span
                                className="flex items-center"
                                data-testid="contact-submitting"
                              >
                                <svg
                                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                Sending...
                              </span>
                            ) : (
                              "Send Message"
                            )}
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                )}
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

ContactForm.propTypes = {
  showContactForm: PropTypes.bool.isRequired,
  setShowContactForm: PropTypes.func.isRequired,
  formSubmitted: PropTypes.bool.isRequired,
  setAlertMessage: PropTypes.func.isRequired,
  setShowAlert: PropTypes.func.isRequired,
  setFormSubmitted: PropTypes.func.isRequired,
  initialMessage: PropTypes.string,
  setAlertType: PropTypes.func,
};

export default ContactForm;
